import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), maxWidth: 760 },
  paper: { padding: theme.spacing(3), marginTop: theme.spacing(2) },
  field: { marginBottom: theme.spacing(2) },
  help: { color: theme.palette.text.secondary, fontSize: 12, marginTop: -8, marginBottom: theme.spacing(2) },
  sectionTitle: { fontWeight: 700, marginTop: theme.spacing(2), marginBottom: theme.spacing(1) },
  providerRow: { marginBottom: theme.spacing(1) },
  actions: { display: "flex", gap: theme.spacing(2), marginTop: theme.spacing(3), flexWrap: "wrap" },
}));

const PROVIDERS = [
  { key: "openai", label: "OpenAI" },
  { key: "anthropic", label: "Anthropic" },
  { key: "gemini", label: "Gemini" },
];

const SuperAdminAI = () => {
  const classes = useStyles();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [enabled, setEnabled] = useState(false);
  // por proveedor: {keySet, enabled} desde el GET
  const [providers, setProviders] = useState({});
  // key tipeada por proveedor (vacío = no cambiar)
  const [keyEdit, setKeyEdit] = useState({});
  // key revelada por proveedor (solo ver; no se envía)
  const [revealed, setRevealed] = useState({});
  const [revealing, setRevealing] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const t = (k, opts) => i18n.t(`superAdminAI.${k}`, opts);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/superadmin/dashboard");
        setCompanies(data.companies || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const fetchConfig = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setKeyEdit({});
    setRevealed({});
    setRevealing({});
    try {
      const { data } = await api.get(`/superadmin/companies/${id}/ai`);
      setEnabled(!!data.enabled);
      setProviders(data.providers || {});
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, []);

  const handleSelectCompany = (id) => {
    setCompanyId(id);
    fetchConfig(id);
  };

  const handleSave = async () => {
    if (!companyId) return;
    setSaving(true);
    try {
      const payloadProviders = {};
      PROVIDERS.forEach(({ key }) => {
        const entry = {};
        if ((keyEdit[key] || "").trim()) entry.key = keyEdit[key].trim();
        if (providers[key]) entry.enabled = providers[key].enabled === true;
        payloadProviders[key] = entry;
      });
      const { data } = await api.put(`/superadmin/companies/${companyId}/ai`, {
        enabled,
        providers: payloadProviders,
      });
      setEnabled(!!data.enabled);
      setProviders(data.providers || {});
      setKeyEdit({});
      setRevealed({});
      setRevealing({});
      toast.success(t("saveOk"));
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const toggleReveal = async (which) => {
    if (revealing[which]) {
      setRevealing((r) => ({ ...r, [which]: false }));
      setRevealed((v) => ({ ...v, [which]: null }));
      return;
    }
    try {
      const { data } = await api.get(
        `/superadmin/companies/${companyId}/ai/reveal-key`,
        { params: { which } }
      );
      setRevealed((v) => ({ ...v, [which]: data.key || "" }));
      setRevealing((r) => ({ ...r, [which]: true }));
    } catch (err) {
      toastError(err);
    }
  };

  const keyPlaceholder = (isSet) => (isSet ? t("keyConfigured") : t("keyNotSet"));

  const renderProvider = ({ key, label }) => {
    const p = providers[key] || {};
    const isRevealing = revealing[key];
    return (
      <div key={key} className={classes.providerRow}>
        <FormControlLabel
          control={
            <Switch
              checked={p.enabled === true}
              onChange={(e) =>
                setProviders((prev) => ({
                  ...prev,
                  [key]: { ...(prev[key] || {}), enabled: e.target.checked },
                }))
              }
              color="primary"
            />
          }
          label={`${label} — ${t("providerEnabled")}`}
        />
        <TextField
          label={t("apiKey", { provider: label })}
          type={isRevealing ? "text" : "password"}
          variant="outlined"
          fullWidth
          size="small"
          className={classes.field}
          value={isRevealing ? (revealed[key] || "") : (keyEdit[key] || "")}
          onChange={(e) =>
            !isRevealing && setKeyEdit((k) => ({ ...k, [key]: e.target.value }))
          }
          placeholder={keyPlaceholder(p.keySet)}
          autoComplete="new-password"
          InputProps={{
            readOnly: isRevealing,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  title={isRevealing ? t("hideKey") : t("showKey")}
                  onClick={() => toggleReveal(key)}
                  disabled={!p.keySet}
                >
                  {isRevealing ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
    );
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5">{t("title")}</Typography>
      <Typography className={classes.help}>{t("subtitle")}</Typography>

      <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
        <InputLabel>{t("company")}</InputLabel>
        <Select
          value={companyId}
          onChange={(e) => handleSelectCompany(e.target.value)}
          label={t("company")}
        >
          <MenuItem value=""><em>{t("selectCompany")}</em></MenuItem>
          {companies.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name} (ID {c.id})</MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading && <CircularProgress size={28} />}

      {!loading && companyId && (
        <Paper className={classes.paper}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="primary"
              />
            }
            label={t("enabled")}
          />
          <div className={classes.help}>{t("enabledHelp")}</div>

          <Divider />
          <Typography className={classes.sectionTitle}>{t("keysSection")}</Typography>
          <Typography className={classes.help}>{t("keysHelp")}</Typography>

          {PROVIDERS.map(renderProvider)}

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={saving}
              onClick={handleSave}
            >
              {t("save")}
            </Button>
          </div>
        </Paper>
      )}
    </div>
  );
};

export default SuperAdminAI;
